import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";

import axiosInstance from "../api/axiosConfig";

interface IApiResponse<T> {
  data: T | null;
  // items: T | null;
  loading: boolean;
  error: Error | null;
  fetchData: () => Promise<T | undefined>;
  options?: IFetchOptions;
}

interface IFetchOptions {
  instance?: any;
  autoFetch?: boolean;
}

export type FetchMethod = "GET" | "POST";

/**
 * Fetch data from API
 * @param url - your API endpoint. This MUST be defined in src/utils/constants/Apis.ts.
 *              Ensure the URL is correctly set up in the constants file before using this hook.
 * @param method HTTP method (GET, POST, PUT, DELETE)
 * @param params request body (for POST, PUT, DELETE)
 * @param query request query (for GET)
 * @param options flag to check if this data need to be automatically fetched or manually fetched
 * @returns {IApiResponse} - data, loading, error, fetchData
 * @example
 * const { data, loading, error, fetchData } = useFetch<Data | Data[]>(API.YOUR_API_ENDPOINT, 'GET');
 */
const useFetch = <T>(
  url: string,
  method: FetchMethod = "GET",
  params?: any,
  query?: any,
  options?: IFetchOptions
): IApiResponse<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadFirstTime, setLoadFirstTime] = useState<boolean>(true);
  const { instance = axiosInstance, autoFetch = true } = options ?? {};
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      let response: AxiosResponse<T>;
      if (!url) {
        return;
      }
      switch (method.toUpperCase()) {
        case "GET":
          response = await instance.get(url, {
            params: { ...params, ...query },
          });
          break;
        case "POST":
          response = await instance.post(url, params);
          break;

        // Add cases for other HTTP methods if needed
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
      setData(response.data);
      // setData(response.data || response?.items)

      return response.data;
    } catch (error) {
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadFirstTime && !autoFetch) {
      return;
    }
    if (url) {
      fetchData();
    }

    // Cleanup function
    return () => {
      // Cleanup logic if needed
    };
  }, [url, method, params, query, autoFetch]);

  useEffect(() => {
    setLoadFirstTime(false);
  }, []);

  return { data, loading, error, fetchData };
};

export default useFetch;
